import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const StrategyBand = withListOverflow(StrategyBandListOwner);
// src/features/plan/StrategyBand.jsx
//
// PLAN-STRATEGY-BAND-S1: Collapsible band showing leadership strategic
// directives. Sits between the toolbar row and the PlanMatrix on the Plan
// view only.
//
// Props:
//   directives      {Array}    — list of directive objects from getDirectives()
//   canEdit         {boolean}  — whether write controls (+ Add directive) are shown
//   onEditDirective {function} — called with (directive|null); null = new directive
//
// Toggle state is persisted in sessionStorage so it survives same-session
// navigation but resets on a new session.

import React, { useState, useEffect } from "react";
import { T } from "../../app/tokens.js";
import { PlanExpandCaret } from "./PlanExpandCaret.jsx";

const SESSION_KEY = "plan:strategyBandOpen";

// ─── Golden header shorthands (Option B) ─────────────────────────────────────
// Named via T.strategy* tokens in tokens.js (distinct from T.gold #d3a000).
const GOLD_HEADER_BG     = T.strategyHeaderBg;
const GOLD_HEADER_BORDER = T.strategyHeaderBorder;
const GOLD_LABEL_COLOR   = T.strategyLabelColor;

// ─── Timeframe sort order ─────────────────────────────────────────────────────
const TIMEFRAME_ORDER = ["now", "lt_1yr", "1_2yr", "2_3yr", "3_plus_yr"];

// ─── Active / inactive status split ──────────────────────────────────────────
const ACTIVE_STATUSES = new Set(["aspiration", "in_review", "approved", "in_motion"]);

// ─── Type / timeframe / status label maps ────────────────────────────────────

export const DIRECTIVE_TYPE_LABELS = {
  hub_designation:    "Hub designation",
  exit:               "Exit",
  new_market:         "New market",
  relocate_upgrade:   "Relocate & upgrade",
  capability_migration: "Capability migration",
};

export const DIRECTIVE_TIMEFRAME_LABELS = {
  now:       "Now",
  lt_1yr:    "< 1 yr",
  "1_2yr":   "1-2 yr",
  "2_3yr":   "2-3 yr",
  "3_plus_yr": "3+ yr",
};

export const DIRECTIVE_DRIVER_LABELS = {
  cost:        "Cost",
  capacity:    "Capacity",
  talent:      "Talent",
  brand:       "Brand",
  compliance:  "Compliance",
  strategy:    "Strategy",
};

export const DIRECTIVE_STATUS_LABELS = {
  aspiration: "Aspiration",
  in_review:  "In review",
  approved:   "Approved",
  in_motion:  "In motion",
  complete:   "Complete",
  cancelled:  "Cancelled",
};

// ─── Act-now logic ────────────────────────────────────────────────────────────
// A directive is "act-now" when it is approved or in_motion AND timeframe is
// strictly "now" (lt_1yr no longer qualifies).

export function isActNow(d) {
  return (d.status === "approved" || d.status === "in_motion")
    && d.timeframe === "now";
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────

function sortDirectives(list) {
  return [...list].sort((a, b) => {
    const tA = TIMEFRAME_ORDER.indexOf(a.timeframe);
    const tB = TIMEFRAME_ORDER.indexOf(b.timeframe);
    const tOrd = (tA === -1 ? 999 : tA) - (tB === -1 ? 999 : tB);
    if (tOrd !== 0) return tOrd;
    return (a.title ?? "").localeCompare(b.title ?? "");
  });
}

// ─── Type colors (from the mockup palette — matched to T.* tokens) ───────────

const TYPE_STYLE = {
  hub_designation:    { color: T.teal,         bg: T.tealSoftBg },
  exit:               { color: T.ragRed,        bg: T.ragRedBg   },
  new_market:         { color: T.blue,          bg: T.blueSoft   },
  relocate_upgrade:   { color: T.purple,        bg: T.purpleBg   },
  capability_migration: { color: T.amberDark,   bg: T.utilAmber  },
};

// ─── Status colors ────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  aspiration: { color: T.mid,          bg: T.lightGray },
  in_review:  { color: T.mid,          bg: T.lightGray },
  approved:   { color: T.utilGreenText, bg: T.utilGreen },
  in_motion:  { color: T.blue,         bg: T.blueSoft  },
  complete:   { color: T.utilGreenText, bg: T.utilGreen },
  cancelled:  { color: T.light,        bg: T.lightGray },
};

// ─── TypeChip ────────────────────────────────────────────────────────────────

function TypeChip({ type }) {
  const style = TYPE_STYLE[type] ?? { color: T.mid, bg: T.lightGray };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 6, padding: "2px 10px", fontSize: T.fsXs, fontWeight: T.fwSemibold, whiteSpace: "nowrap", color: style.color, background: style.bg }}>
      {DIRECTIVE_TYPE_LABELS[type] ?? type}
    </span>
  );
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const style = STATUS_STYLE[status] ?? { color: T.mid, bg: T.lightGray };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 6, padding: "2px 10px", fontSize: T.fsXs, fontWeight: T.fwSemibold, whiteSpace: "nowrap", color: style.color, background: style.bg }}>
      {DIRECTIVE_STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ─── Markets cell text ────────────────────────────────────────────────────────

function marketsCellText(directive, nameMap = new Map()) {
  if (!directive.markets || directive.markets.length === 0) return "—";
  return directive.markets
    .map((m) => {
      const name = nameMap.get(m.marketId.toLowerCase()) ?? m.marketId.toUpperCase();
      return m.prospectLabel ? `${name} → ${m.prospectLabel}` : name;
    })
    .join(", ");
}

// ─── Shared golden header row ─────────────────────────────────────────────────

function BandHeader({ directives, actNowCount, isExpanded, onToggle }) {
  return (
    <div
      data-testid={isExpanded ? "strategy-band-toggle-header" : "strategy-band-collapsed"}
      style={{
        display:         "flex",
        alignItems:      "center",
        gap:             10,
        padding:         isExpanded ? "3px 16px" : "1px 16px",
        cursor:          "pointer",
        userSelect:      "none",
        background:      GOLD_HEADER_BG,
        borderBottom:    isExpanded ? `1px solid ${GOLD_HEADER_BORDER}` : "none",
      }}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? "Hide strategy directives" : "Show strategy directives"}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
    >
      <span style={{
        fontSize:      T.fsXxs,
        fontWeight:    T.fwBold,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color:         GOLD_LABEL_COLOR,
        display:       "inline-flex",
        alignItems:    "center",
        gap:           5,
        flexShrink:    0,
      }}>
        ◆ Strategy
      </span>
      <span style={{ fontSize: T.fsSm, color: T.dark, fontWeight: T.fwSemibold, flexShrink: 0 }}>
        {directives.length} {directives.length === 1 ? "directive" : "directives"}
      </span>
      {!isExpanded && actNowCount > 0 && (
        <span
          data-testid="strategy-band-act-now-chip"
          style={{
            display:      "inline-flex",
            alignItems:   "center",
            fontSize:     T.fsXs,
            fontWeight:   T.fwSemibold,
            borderRadius: 6,
            padding:      "2px 10px",
            background:   T.ragRedBg,
            color:        T.ragRed,
            flexShrink:   0,
          }}
        >
          {actNowCount} act-now
        </span>
      )}
      <div style={{ flex: 1 }} />
      <span style={{
        display:    "inline-flex",
        alignItems: "center",
        gap:        4,
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}>
        <span style={{
          fontSize:   T.fsXs,
          fontWeight: T.fwSemibold,
          color:      "var(--v2-accent)",
        }}>
          {isExpanded ? "Hide" : "Show directives"}
        </span>
        <PlanExpandCaret isExpanded={isExpanded} />
      </span>
    </div>
  );
}

// ─── Expanded directive table ─────────────────────────────────────────────────

function ExpandedBand({ directives, canEdit, onCollapse, onEditDirective, density = "comfortable", nameMap = new Map() }) {
  const [showInactive, setShowInactive] = useState(false);

  const active   = sortDirectives(directives.filter((d) => ACTIVE_STATUSES.has(d.status)));
  const inactive = directives.filter((d) => !ACTIVE_STATUSES.has(d.status));
  const completeCount  = inactive.filter((d) => d.status === "complete").length;
  const cancelledCount = inactive.filter((d) => d.status === "cancelled").length;

  const visibleRows = showInactive ? [...active, ...inactive] : active;

  const thStyle = {
    fontSize:      T.fsXxs,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color:         "var(--v2-mid)",
    fontWeight:    T.fwBold,
    padding:       "10px 14px",
    textAlign:     "left",
    background:    T.lightGray,
    borderBottom:  "1px solid var(--v2-line)",
    whiteSpace:    "nowrap",
  };
  const tdStyle = {
    fontSize:      T.fsSm,
    color:         T.dark,
    padding:       density === "compact" ? "5px 12px" : "9px 12px",
    borderBottom:  `1px solid ${T.lightGray}`,
    verticalAlign: "middle",
  };

  return (
    <>
      <div
        data-testid="strategy-band-table"
        style={{
          maxHeight: 340,
          overflowY: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsXs }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: "28%", textAlign: "left" }}>Directive</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Type</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Markets</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Timeframe</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((d) => (
              <tr
                key={d.id}
                data-testid={`directive-row-${d.id}`}
                style={{ cursor: "pointer" }}
                onClick={() => onEditDirective(d)}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
              >
                <td style={{ ...tdStyle, fontWeight: T.fwSemibold }}>
                  <span>{d.title}</span>
                  {isActNow(d) && (
                    <span
                      data-testid={`act-now-marker-${d.id}`}
                      style={{
                        marginLeft:  6,
                        fontSize:    T.fsXxs,
                        fontWeight:  T.fwSemibold,
                        color:       T.ragRed,
                        whiteSpace:  "nowrap",
                      }}
                    >
                      ● Act now
                    </span>
                  )}
                </td>
                <td style={tdStyle}>
                  <TypeChip type={d.type} />
                </td>
                <td style={{ ...tdStyle, color: T.mid }}>
                  {marketsCellText(d, nameMap)}
                </td>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                  {DIRECTIVE_TIMEFRAME_LABELS[d.timeframe] ?? d.timeframe}
                </td>
                <td style={tdStyle}>
                  <StatusBadge status={d.status} />
                </td>
              </tr>
            ))}
            {active.length === 0 && inactive.length === 0 && (
              <tr>
                <td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: T.light }}>
                  No directives yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{
        padding:        "9px 16px",
        fontSize:       T.fsXs,
        color:          T.mid,
        display:        "flex",
        justifyContent: "space-between",
        alignItems:     "center",
        borderTop:      (active.length + inactive.length) > 0 ? `1px solid ${T.border}` : "none",
      }}>
        <span>
          {inactive.length > 0 && (
            <button
              type="button"
              data-testid="strategy-band-inactive-toggle"
              onClick={(e) => { e.stopPropagation(); setShowInactive((v) => !v); }}
              style={{
                background:  "none",
                border:      "none",
                padding:     0,
                cursor:      "pointer",
                color:       T.mid,
                fontSize:    T.fsXs,
                fontWeight:  T.fwSemibold,
                fontFamily:  "inherit",
                textDecoration: "underline",
                whiteSpace:  "nowrap",
              }}
            >
              {`Completed (${completeCount}) · Cancelled (${cancelledCount}) — ${showInactive ? "hide" : "show"}`}
            </button>
          )}
          {inactive.length === 0 && (
            <span>Directives drive the per-market Strategy column below and feed Recommendations.</span>
          )}
        </span>
        {canEdit && (
          <button
            type="button"
            data-testid="strategy-band-add-directive"
            onClick={(e) => { e.stopPropagation(); onEditDirective(null); }}
            style={{
              background:  "none",
              border:      "none",
              padding:     0,
              cursor:      "pointer",
              color:       "var(--v2-accent)",
              fontSize:    T.fsSm,
              fontWeight:  T.fwSemibold,
              fontFamily:  "inherit",
              whiteSpace:  "nowrap",
            }}
          >
            + Add directive
          </button>
        )}
      </div>
    </>
  );
}

// ─── Exported StrategyBand ────────────────────────────────────────────────────

function StrategyBandListOwner({ directives = [], canEdit = false, onEditDirective, masterExpanded = null, density = "comfortable", markets = [] }) {
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === "true"; } catch { return false; }
  });

  function setAndPersist(value) {
    setOpen(value);
    try { sessionStorage.setItem(SESSION_KEY, String(value)); } catch { /* ignore */ }
  }

  // MARKET-PLAN-EXPAND-ALL-INCLUDE-STRATEGY: when the master expand-all switch
  // fires, masterExpanded changes to a new {expanded, seq} event object. seq
  // increments on every actuation so this effect fires even when expanded polarity
  // stays the same across a manual intermediate state change. null = not set yet.
  useEffect(() => {
    if (masterExpanded === null) return;
    setAndPersist(masterExpanded.expanded);
  }, [masterExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!directives) return null;

  const nameMap = new Map(
    (markets ?? []).map((m) => [m.id.toLowerCase(), m.name])
  );

  const actNowCount = directives.filter(isActNow).length;

  return (
    <div
      data-testid="strategy-band"
      style={{
        background:    T.white,
        border:        `1px solid ${T.border}`,
        borderRadius:  12,
        boxShadow:     "0 1px 2px rgba(16,36,58,.05)",
        marginBottom:  12,
        overflow:      "hidden",
      }}
    >
      <BandHeader
        directives={directives}
        actNowCount={actNowCount}
        isExpanded={open}
        onToggle={() => setAndPersist(!open)}
      />

      {open && (
        <ExpandedBand
          directives={directives}
          canEdit={canEdit}
          onCollapse={() => setAndPersist(false)}
          onEditDirective={onEditDirective}
          density={density}
          nameMap={nameMap}
        />
      )}
    </div>
  );
}
